import type { FC, ReactNode } from 'react';
import type { StructuredInfoItemProps } from '@george-labs.com/design-system/types/components/Info/StructuredInfo/StructuredInfoItem';
import { isValidElement } from 'react';
import {
    AccountNumber,
    Card,
    Copy,
    Inline,
    StructuredInfo,
    StructuredInfoItem,
} from '@george-labs.com/design-system';

import type { Amount } from '@shared/core-types';
import { useGlobalColor } from '@shared/core-ui-layout';
import { LocalizedAmount } from '@shared/core-ui-amount';
import { ConditionalWrapper } from '@luna/utils-ui';
import { useNotifications } from '@shared/snackbar';

type Props = {
    alias: string;
    avatar: ReactNode;
    badges?: ReactNode;
    mainAmount?: Amount | ReactNode;
    productType?: string;
    productIdentifier?: string;
    productIdentifierAriaLabel?: string;
    productIdentifierTruncate?: boolean;
    productIdentifierTooltip?: string;
    productIdentifierCopySuccessMessage?: string;
    'data-cy': string;
    secondaryAmount?: Amount | ReactNode;
    secondaryAmountPostfix?: string;
    tertiaryAmount?: Amount | ReactNode;
    tertiaryAmountPostfix?: string;
    customContent?: ReactNode;
    customContentProps?: Partial<Omit<StructuredInfoItemProps, 'children'>>;
};

export const LayoutStructuredInfo: FC<Props> = ({
    alias,
    badges,
    productIdentifier,
    productIdentifierAriaLabel,
    productIdentifierTruncate,
    productIdentifierTooltip,
    productIdentifierCopySuccessMessage = '',
    productType,
    avatar,
    mainAmount,
    'data-cy': dataCy,
    secondaryAmount,
    secondaryAmountPostfix,
    tertiaryAmount,
    tertiaryAmountPostfix,
    customContent,
    customContentProps = {},
}) => {
    const { value } = useGlobalColor();
    const { displaySuccess } = useNotifications('layout-product-info');

    const isAliasSameAsProductType = productType === alias;

    const mainAmountComponent =
        mainAmount && isValidElement(mainAmount) ? (
            mainAmount
        ) : (
            <LocalizedAmount amount={mainAmount as Amount} coloured showNegativeInRedColor />
        );

    const secondaryAmountComponent =
        secondaryAmount && isValidElement(secondaryAmount) ? (
            secondaryAmount
        ) : (
            <LocalizedAmount
                amount={secondaryAmount as Amount}
                inheritFontWeight
                color={LocalizedAmount.COLOR.FADED}
            />
        );

    const tertiaryAmountComponent =
        tertiaryAmount && isValidElement(tertiaryAmount) ? (
            tertiaryAmount
        ) : (
            <LocalizedAmount
                amount={tertiaryAmount as Amount}
                inheritFontWeight
                color={LocalizedAmount.COLOR.FADED}
            />
        );

    const handleAccountNumberCopy = () => {
        displaySuccess(productIdentifierCopySuccessMessage);
    };

    return (
        <Card>
            <StructuredInfo
                data-cy={dataCy}
                alignment={StructuredInfo.ALIGNMENT.RIGHT}
                color={value}
                avatar={avatar}
                header={
                    <>
                        {!isAliasSameAsProductType && productType && (
                            <StructuredInfoItem
                                data-cy={`${dataCy}-product-type`}
                                size={StructuredInfoItem.SIZE.SMALL}
                                faded
                            >
                                {productType}
                            </StructuredInfoItem>
                        )}
                        {alias && (
                            <StructuredInfoItem data-cy={`${dataCy}-alias`} className="mb-1">
                                {alias}
                            </StructuredInfoItem>
                        )}
                    </>
                }
            >
                {productIdentifier && (
                    <StructuredInfoItem
                        data-cy={`${dataCy}-product-identifier`}
                        size={StructuredInfoItem.SIZE.SMALL}
                        faded
                    >
                        <ConditionalWrapper
                            condition={Boolean(productIdentifierTooltip)}
                            wrapper={Copy}
                            wrapperProps={{
                                label: productIdentifierTooltip,
                                onCopy: handleAccountNumberCopy,
                                ariaLabel: `${productIdentifierAriaLabel || productIdentifier} ${productIdentifierTooltip}`,
                            }}
                        >
                            <AccountNumber
                                ariaLabel={productIdentifierAriaLabel}
                                truncate={productIdentifierTruncate}
                            >
                                {productIdentifier}
                            </AccountNumber>
                        </ConditionalWrapper>
                    </StructuredInfoItem>
                )}
                {mainAmount && (
                    <StructuredInfoItem data-cy={`${dataCy}-main-amount`}>
                        {mainAmountComponent}
                    </StructuredInfoItem>
                )}
                {secondaryAmount && (
                    <StructuredInfoItem
                        data-cy={`${dataCy}-secondary-amount`}
                        size={StructuredInfoItem.SIZE.SMALL}
                        faded
                        truncate={false}
                    >
                        {secondaryAmountComponent}{' '}
                        {secondaryAmountPostfix && (
                            <Inline data-cy={`${dataCy}-secondary-amount-postfix`}>
                                {secondaryAmountPostfix}
                            </Inline>
                        )}
                    </StructuredInfoItem>
                )}
                {tertiaryAmount && (
                    <StructuredInfoItem
                        data-cy={`${dataCy}-tertiary-amount`}
                        size={StructuredInfoItem.SIZE.SMALL}
                        faded
                        truncate={false}
                    >
                        {tertiaryAmountComponent}{' '}
                        {tertiaryAmountPostfix && (
                            <Inline data-cy={`${dataCy}-tertiary-amount-postfix`}>
                                {tertiaryAmountPostfix}
                            </Inline>
                        )}
                    </StructuredInfoItem>
                )}
                {customContent && (
                    <StructuredInfoItem data-cy={`${dataCy}-custom`} {...customContentProps}>
                        {customContent}
                    </StructuredInfoItem>
                )}
                {badges && (
                    <StructuredInfoItem data-cy={`${dataCy}-badges`} truncate={false}>
                        {badges}
                    </StructuredInfoItem>
                )}
            </StructuredInfo>
        </Card>
    );
};
